import { useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { SERVICE_AREAS, APPROVAL_PATHS, isActiveProfile } from './constants'

export default function BulkTaskModal({ clients, profiles, currentUser, onClose, onSaved }) {
  const toast = useToast()
  // mode: 'multi_task'  = same client+assignee, many different tasks (original)
  //       'multi_person'= one task definition, replicated to each selected person
  const [mode, setMode] = useState('multi_task')
  const [clientId, setClientId] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [serviceArea, setServiceArea] = useState('website')
  const [approvalPath, setApprovalPath] = useState('internal')
  const [dateDue, setDateDue] = useState('')
  const [weekNumber, setWeekNumber] = useState('')
  const [isOnboarding, setIsOnboarding] = useState(false)
  const [tasks, setTasks] = useState([
    { title: '', notes: '' },
    { title: '', notes: '' },
    { title: '', notes: '' },
  ])
  const [saving, setSaving] = useState(false)

  // multi_person mode: a single shared task definition + the people to give it to
  const [sharedTask, setSharedTask] = useState({ title: '', notes: '' })
  const [sharedDocLink, setSharedDocLink] = useState('')
  const [selectedPeople, setSelectedPeople] = useState([])

  const togglePerson = (id) => {
    setSelectedPeople(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const updateTask = (i, field, val) => {
    setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t))
  }
  const addRow = () => setTasks(prev => [...prev, { title: '', notes: '' }])
  const removeRow = (i) => setTasks(prev => prev.filter((_, idx) => idx !== i))

  // ── Mode A: many tasks, one assignee ───────────────────────
  const saveMultiTask = async () => {
    const valid = tasks.filter(t => t.title.trim())
    if (!valid.length) { toast('Add at least one task title', 'error'); return }
    if (!clientId) { toast('Select a client', 'error'); return }
    setSaving(true)
    try {
      const inserts = valid.map(t => ({
        title: t.title.trim(),
        notes: t.notes || null,
        client_id: clientId,
        assigned_to: assignedTo || null,
        assigned_by: currentUser.id,
        service_area: serviceArea,
        approval_path: approvalPath,
        date_due: dateDue || null,
        week_number: weekNumber ? parseInt(weekNumber) : null,
        is_onboarding_task: isOnboarding,
        status: 'not_started',
        client_visible: true,
        collaborator_ids: [],
        deliverable_links: [],
        google_doc_links: [],
        loom_links: [],
      }))
      const { data, error } = await supabase.from('tasks').insert(inserts).select()
      if (error) throw error
      for (const t of data || []) {
        supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: t.id } })
      }
      toast(`${valid.length} tasks created!`, 'success')
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  // ── Mode B: one task, replicated to each selected person ───
  const saveMultiPerson = async () => {
    if (!sharedTask.title.trim()) { toast('Add a task title', 'error'); return }
    if (!clientId) { toast('Select a client', 'error'); return }
    if (selectedPeople.length === 0) { toast('Select at least one person', 'error'); return }
    setSaving(true)
    try {
      // One row per selected person — same title/notes/settings, different assignee.
      const inserts = selectedPeople.map(personId => ({
        title: sharedTask.title.trim(),
        notes: sharedTask.notes || null,
        client_id: clientId,
        assigned_to: personId,
        assigned_by: currentUser.id,
        service_area: serviceArea,
        approval_path: approvalPath,
        date_due: dateDue || null,
        week_number: weekNumber ? parseInt(weekNumber) : null,
        is_onboarding_task: isOnboarding,
        status: 'not_started',
        client_visible: true,
        collaborator_ids: [],
        deliverable_links: [],
        google_doc_links: sharedDocLink.trim() ? [sharedDocLink.trim()] : [],
        loom_links: [],
      }))
      const { data, error } = await supabase.from('tasks').insert(inserts).select()
      if (error) throw error
      for (const t of data || []) {
        supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: t.id } })
      }
      toast(`Task assigned to ${selectedPeople.length} ${selectedPeople.length === 1 ? 'person' : 'people'}!`, 'success')
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const save = () => mode === 'multi_person' ? saveMultiPerson() : saveMultiTask()

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Bulk Add Tasks</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {mode === 'multi_person'
                ? 'One task — assign the same task to several people at once'
                : 'Same client, same assignee — add multiple tasks at once'}
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: 'var(--cream)', padding: 4, borderRadius: 10 }}>
            <button
              className={`btn btn-sm ${mode === 'multi_task' ? 'btn-gold' : 'btn-ghost'}`}
              style={{ flex: 1 }}
              onClick={() => setMode('multi_task')}
            >
              Many tasks → one person
            </button>
            <button
              className={`btn btn-sm ${mode === 'multi_person' ? 'btn-gold' : 'btn-ghost'}`}
              style={{ flex: 1 }}
              onClick={() => setMode('multi_person')}
            >
              One task → many people
            </button>
          </div>

          {/* Shared settings */}
          <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
              {mode === 'multi_person' ? 'Applied to every copy of this task' : 'Applied to all tasks below'}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client *</label>
                <select className="form-control" value={clientId} onChange={e => setClientId(e.target.value)}>
                  <option value="">Select client...</option>
                  <option value="">🏢 Internal (no client)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {mode === 'multi_task' && (
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-control" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                    <option value="">Unassigned</option>
                    {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Service Area</label>
                <select className="form-control" value={serviceArea} onChange={e => setServiceArea(e.target.value)}>
                  {SERVICE_AREAS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Approval Path</label>
                <select className="form-control" value={approvalPath} onChange={e => setApprovalPath(e.target.value)}>
                  {APPROVAL_PATHS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input className="form-control" type="date" value={dateDue} onChange={e => setDateDue(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Week #</label>
                <input className="form-control" type="number" min="1" value={weekNumber} onChange={e => setWeekNumber(e.target.value)} placeholder="e.g. 1" />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={isOnboarding} onChange={e => setIsOnboarding(e.target.checked)} />
              Onboarding tasks
            </label>
          </div>

          {mode === 'multi_person' ? (
            <>
              {/* Single task definition */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>The task</div>
              <div className="form-group">
                <label className="form-label">Task title *</label>
                <input className="form-control" value={sharedTask.title}
                  onChange={e => setSharedTask(t => ({ ...t, title: e.target.value }))}
                  placeholder="e.g. Write your assigned section of the Q2 report" />
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea className="form-control" rows={2} value={sharedTask.notes}
                  onChange={e => setSharedTask(t => ({ ...t, notes: e.target.value }))}
                  placeholder="Shared context — e.g. which doc to work in, how the work is split…" />
              </div>
              <div className="form-group">
                <label className="form-label">Shared document link (optional)</label>
                <input className="form-control" value={sharedDocLink}
                  onChange={e => setSharedDocLink(e.target.value)}
                  placeholder="https://docs.google.com/... (the larger doc this task references)" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Added to each person's copy so everyone references the same document.
                </div>
              </div>

              {/* People picker */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 16, marginBottom: 10 }}>
                Assign to ({selectedPeople.length} selected)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {profiles.filter(isActiveProfile).map(p => {
                  const checked = selectedPeople.includes(p.id)
                  return (
                    <label key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      padding: '8px 10px', borderRadius: 8, fontSize: 13,
                      background: checked ? '#e8f5eb' : 'var(--cream)',
                      border: `1px solid ${checked ? '#c2dec8' : 'var(--border-light)'}`,
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => togglePerson(p.id)} />
                      <span style={{ fontWeight: checked ? 600 : 400 }}>{p.full_name}</span>
                    </label>
                  )
                })}
              </div>
            </>
          ) : (
            <>
              {/* Task rows */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Tasks</div>
              {tasks.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <div style={{ width: 24, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <input className="form-control" value={t.title} onChange={e => updateTask(i, 'title', e.target.value)}
                      placeholder={`Task ${i + 1} title...`}
                      onKeyDown={e => e.key === 'Enter' && i === tasks.length - 1 && addRow()}
                      style={{ marginBottom: 4 }} />
                    <input className="form-control" value={t.notes} onChange={e => updateTask(i, 'notes', e.target.value)}
                      placeholder="Notes (optional)..." style={{ fontSize: 12 }} />
                  </div>
                  {tasks.length > 1 && (
                    <button className="btn-icon" onClick={() => removeRow(i)} style={{ marginTop: 8 }}><Trash2 size={13} /></button>
                  )}
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={addRow} style={{ marginTop: 4 }}>
                <Plus size={13} /> Add another task
              </button>
            </>
          )}
        </div>
        <div className="modal-footer">
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
            {mode === 'multi_person'
              ? `${selectedPeople.length} ${selectedPeople.length === 1 ? 'person' : 'people'} selected`
              : `${tasks.filter(t => t.title.trim()).length} task${tasks.filter(t => t.title.trim()).length !== 1 ? 's' : ''} ready to create`}
          </div>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>
            {saving ? 'Creating...'
              : mode === 'multi_person'
                ? `Create ${selectedPeople.length} ${selectedPeople.length === 1 ? 'copy' : 'copies'}`
                : `Create ${tasks.filter(t => t.title.trim()).length} Tasks`}
          </button>
        </div>
      </div>
    </div>
  )
}